import { Injectable, computed, signal } from '@angular/core';

export type AdminFilterRange = '7' | '30' | '90' | 'custom';

export interface AdminFilterQueryParams {
  range: AdminFilterRange;
  from?: string;
  to?: string;
}

/**
 * Shared filter state for ops/admin pages: time range and an optional
 * custom date window. Exposed as signals so components and services can read
 * and derive query params.
 */
@Injectable({ providedIn: 'root' })
export class AdminFilterService {
  private readonly _range = signal<AdminFilterRange>('30');
  private readonly _customFrom = signal<string>('');
  private readonly _customTo = signal<string>('');

  readonly range = this._range.asReadonly();
  readonly customFrom = this._customFrom.asReadonly();
  readonly customTo = this._customTo.asReadonly();

  readonly rangeLabel = computed<string>(() => {
    const r = this._range();
    if (r === 'custom') {
      const from = this._customFrom();
      const to = this._customTo();
      if (from && to) return `${from} – ${to}`;
      return 'custom range';
    }
    return r === '7' ? 'last 7 days' : r === '30' ? 'last 30 days' : 'last 90 days';
  });

  readonly queryParams = computed<AdminFilterQueryParams>(() => {
    const range = this._range();
    const params: AdminFilterQueryParams = { range };
    if (range === 'custom') {
      const from = this._customFrom();
      const to = this._customTo();
      if (from) params.from = from;
      if (to) params.to = to;
    }
    return params;
  });

  setRange(range: AdminFilterRange): void {
    this._range.set(range);
  }

  setCustomFrom(value: string): void {
    this._customFrom.set(value);
  }

  setCustomTo(value: string): void {
    this._customTo.set(value);
  }

  reset(): void {
    this._range.set('30');
    this._customFrom.set('');
    this._customTo.set('');
  }
}
